import { useEffect, useState } from 'react'
import { useNavigate, useSearchParams } from 'react-router-dom'
import { useAuth } from '@/lib/auth-context'
import { verifyMagicLink, mapVerifyResponseToUser } from '@/lib/api/auth'
import { landingPageFor } from '@/lib/landing'
import { Loader2, CheckCircle, XCircle } from 'lucide-react'
import { Button } from '@/components/ui/button'

export default function VerifyPage() {
  const navigate = useNavigate()
  const [searchParams] = useSearchParams()
  const { login } = useAuth()

  const token = searchParams.get('token')

  // Initialize state based on token presence — no synchronous setState needed in effect
  const [status, setStatus] = useState<'loading' | 'success' | 'error'>(() =>
    token ? 'loading' : 'error'
  )
  const [errorMessage, setErrorMessage] = useState(() =>
    token ? '' : 'Lien de connexion invalide'
  )

  useEffect(() => {
    if (!token) return

    async function verifyToken(t: string) {
      try {
        const response = await verifyMagicLink(t)
        const user = mapVerifyResponseToUser(response)
        await login(user)
        setStatus('success')
        const landingPage = landingPageFor(user)
        setTimeout(() => {
          navigate(landingPage)
        }, 1500)
      } catch (error) {
        setStatus('error')
        setErrorMessage(
          error instanceof Error
            ? error.message
            : 'Le lien de connexion est invalide ou a expiré'
        )
      }
    }

    verifyToken(token)
  }, [searchParams, login, navigate, token])

  return (
    <div className="min-h-screen bg-background flex items-center justify-center p-4">
      <div className="w-full max-w-md">
        {/* Logo */}
        <div className="text-center mb-8">
          <h1 className="text-5xl font-bold text-primary mb-4">SOLY</h1>
          <div className="w-24 h-1 bg-primary mx-auto rounded-full"></div>
        </div>

        {/* Status card */}
        <div className="bg-white rounded-2xl shadow-xl p-8 border border-gray-100">
          {status === 'loading' && (
            <div className="text-center">
              <div className="inline-flex items-center justify-center w-16 h-16 bg-muted rounded-full mb-4">
                <Loader2 className="w-8 h-8 text-foreground animate-spin" />
              </div>
              <h2 className="text-xl font-semibold text-gray-900 mb-2">
                Vérification en cours...
              </h2>
              <p className="text-muted-foreground">
                Veuillez patienter
              </p>
            </div>
          )}

          {status === 'success' && (
            <div className="text-center">
              <div className="inline-flex items-center justify-center w-16 h-16 bg-green-100 rounded-full mb-4 animate-scale-in">
                <CheckCircle className="w-8 h-8 text-green-600" />
              </div>
              <h2 className="text-xl font-semibold text-gray-900 mb-2">
                Connexion réussie !
              </h2>
              <p className="text-muted-foreground">
                Redirection vers le dashboard...
              </p>
            </div>
          )}

          {status === 'error' && (
            <div className="text-center">
              <div className="inline-flex items-center justify-center w-16 h-16 bg-red-100 rounded-full mb-4">
                <XCircle className="w-8 h-8 text-red-600" />
              </div>
              <h2 className="text-xl font-semibold text-gray-900 mb-2">
                Erreur de connexion
              </h2>
              <p className="text-muted-foreground mb-6">
                {errorMessage}
              </p>
              <Button
                onClick={() => navigate('/')}
                className="w-full"
              >
                Retour à la connexion
              </Button>
            </div>
          )}
        </div>
      </div>
    </div>
  )
}
